import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Icon,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Strong,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';

export type DeleteSyncModalData = {
  name: string;
  /** What the sync does, in the progressive form ("syncing people to …"). */
  summary: ReactNode;
  /** Deletes the sync and leaves the page; resolves to whether it went through. */
  onDelete: () => Promise<boolean>;
};

/** Ember's `confirm-and-delete`: what stops, what goes, and the question. Managed modal; data in, no context. */
export function DeleteSyncModal({ data, onConfirm, onClose, modalState }: PlumaManagedModalProps<DeleteSyncModalData>) {
  const { name, summary, onDelete } = data;
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    void onDelete().then((deleted) => {
      setIsDeleting(false);
      if (deleted) {
        onConfirm();
      }
    });
  };

  return (
    <Modal
      state={modalState}
      title={`Delete ${name}`}
      onClose={onClose}
      isDismissable={!isDeleting}
      data-test-sql-imports-modal="confirm-and-delete"
    >
      <ModalBody>
        <InlineStack gap="100" alignBlock="start">
          <Icon name="critical" color="critical" />
          <Box>
            <Text>Stop</Text> {summary}
          </Box>
        </InlineStack>
        <InlineStack gap="100" alignBlock="start" mt="200">
          <Icon name="critical" color="critical" />
          <Paragraph>Delete sync settings, SQL query, and historical imports.</Paragraph>
        </InlineStack>
        <Paragraph mt="200">
          Are you sure you want to delete <Strong>{name}</Strong>?
        </Paragraph>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={isDeleting}
          onClick={handleDelete}
          data-test-sql-import-button="confirm-delete"
        >
          Delete sync
        </Button>
      </ModalFooter>
    </Modal>
  );
}
